import { useRef, useState, useEffect, useCallback } from "react"
import Webcam from "react-webcam"
import * as faceapi from "face-api.js"
import { checkAccess as apiCheckAccess, openDoor as apiOpenDoor } from "../../services/accessService"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Select } from "../../components/ui/Select"
import { fr } from "../../locales/fr"

const Phase = {
  waiting: "waiting",
  blink: "blink",
  checking: "checking",
  granted: "granted",
  denied: "denied",
  error: "error",
}

const DETECT_INTERVAL_MS = 1400
/** Après un passage (réussi ou refusé), pause avant un nouveau contrôle API (évite rafales check + ouverture porte). */
const SCAN_COOLDOWN_MS = 14000

export default function AccessTerminal() {
  const webcamRef = useRef(null)
  const cooldownUntilRef = useRef(0)

  const [phase, setPhase] = useState(Phase.waiting)
  const [member, setMember] = useState(null)
  const [busy, setBusy] = useState(false)
  const [modelsLoaded, setModelsLoaded] = useState(false)

  const [devices, setDevices] = useState([])
  const [deviceId, setDeviceId] = useState(null)

  useEffect(() => {
    const loadModels = async () => {
      await faceapi.nets.tinyFaceDetector.loadFromUri("/models")
      await faceapi.nets.faceLandmark68Net.loadFromUri("/models")
      await faceapi.nets.faceRecognitionNet.loadFromUri("/models")
      setModelsLoaded(true)
    }
    loadModels()
  }, [])

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((all) => {
      const cams = all.filter((d) => d.kind === "videoinput")
      setDevices(cams)
      if (cams.length > 0) {
        setDeviceId(cams[0].deviceId)
      }
    })
  }, [])

  const isBlinking = (landmarks) => {
    const leftEye = landmarks.getLeftEye()
    const rightEye = landmarks.getRightEye()
    const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)
    const left = distance(leftEye[1], leftEye[5])
    const right = distance(rightEye[1], rightEye[5])
    return left + right < 10
  }

  const runCheck = useCallback(async (descriptor) => {
    setPhase(Phase.checking)
    try {
      const data = await apiCheckAccess(descriptor)
      if (data.status === "granted") {
        setPhase(Phase.granted)
        setMember(data.member?.name ?? null)
        try {
          await apiOpenDoor()
        } catch {
          /* optionnel */
        }
      } else {
        setPhase(Phase.denied)
        setMember(null)
      }
    } catch {
      setPhase(Phase.error)
      setMember(null)
    }
    setTimeout(() => {
      setPhase(Phase.waiting)
    }, 4000)
  }, [])

  const detect = useCallback(async () => {
    if (!modelsLoaded || !webcamRef.current || busy) return
    if (Date.now() < cooldownUntilRef.current) return

    const video = webcamRef.current.video
    if (
      !video ||
      video.readyState !== 4 ||
      video.videoWidth === 0 ||
      video.videoHeight === 0
    ) {
      return
    }

    try {
      const result = await faceapi
        .detectSingleFace(
          video,
          new faceapi.TinyFaceDetectorOptions({
            inputSize: 224,
            scoreThreshold: 0.6,
          })
        )
        .withFaceLandmarks()
        .withFaceDescriptor()

      if (!result) return

      if (!isBlinking(result.landmarks)) {
        setPhase(Phase.blink)
        return
      }

      setBusy(true)
      cooldownUntilRef.current = Date.now() + SCAN_COOLDOWN_MS
      await runCheck(Array.from(result.descriptor))
      setTimeout(() => setBusy(false), 4000)
    } catch (e) {
      console.log("Detection error:", e)
    }
  }, [modelsLoaded, busy, runCheck])

  useEffect(() => {
    if (!modelsLoaded) return
    const interval = setInterval(() => {
      void detect()
    }, DETECT_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [busy, modelsLoaded, detect])

  const statusText = fr.accessTerminal.status[phase]

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">{fr.accessTerminal.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{fr.accessTerminal.subtitle}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{fr.accessTerminal.camera}</CardTitle>
          <CardDescription>{fr.accessTerminal.cameraHint}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {devices.length > 0 && (
            <Select
              value={deviceId || ""}
              onChange={(e) => setDeviceId(e.target.value)}
              className="max-w-md"
            >
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || fr.accessTerminal.cameraFallback(d.deviceId)}
                </option>
              ))}
            </Select>
          )}

          <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
            <Webcam
              ref={webcamRef}
              audio={false}
              playsInline
              videoConstraints={{
                deviceId: deviceId ? { exact: deviceId } : undefined,
              }}
              className="aspect-video w-full object-cover"
            />
          </div>

          <div className="text-center">
            <div
              className={`text-lg font-semibold ${
                phase === Phase.granted
                  ? "text-success"
                  : phase === Phase.denied || phase === Phase.error
                    ? "text-destructive"
                    : "text-muted-foreground"
              }`}
            >
              {statusText}
            </div>
            {member && (
              <p className="mt-2 text-foreground">{fr.accessTerminal.welcome(member)}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
